/*
 * Назначение: вкладка «Промпт и ограничения» в настройках проекта.
 * Функционал: редактирование базового шаблона sandbox-промпта, кнопка
 * сброса к стандартному, дополнительные указания владельца, списки
 * запрещённых тем/обещаний, кнопка предпросмотра итогового промпта.
 */

import { useEffect, useState } from "react";
import { Eye, Loader2, Save } from "lucide-react";
import { toast } from "sonner";

import { projectsApi } from "../../../lib/admin-api";
import { ApiError } from "../../../lib/api";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import type { ProjectDetails } from "../../../lib/types";

import { splitLines, useProjectMutation } from "./shared";

export function PromptTab({ project }: { project: ProjectDetails }) {
  const mutation = useProjectMutation("сохранить промпт");
  const [form, setForm] = useState({
    base_system_prompt: project.base_system_prompt ?? project.base_system_prompt_default,
    base_is_default: project.base_system_prompt === null,
    system_prompt: project.system_prompt ?? "",
    forbidden_topics: (project.forbidden_topics ?? []).join("\n"),
    forbidden_promises: (project.forbidden_promises ?? []).join("\n"),
  });
  const [previewOpen, setPreviewOpen] = useState(false);
  const [preview, setPreview] = useState<{ text: string; length: number } | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    setForm({
      base_system_prompt: project.base_system_prompt ?? project.base_system_prompt_default,
      base_is_default: project.base_system_prompt === null,
      system_prompt: project.system_prompt ?? "",
      forbidden_topics: (project.forbidden_topics ?? []).join("\n"),
      forbidden_promises: (project.forbidden_promises ?? []).join("\n"),
    });
  }, [project]);

  const resetBaseToDefault = () => {
    setForm((s) => ({
      ...s,
      base_system_prompt: project.base_system_prompt_default,
      base_is_default: true,
    }));
  };

  const handlePreview = async () => {
    setPreviewLoading(true);
    try {
      const res = await projectsApi.promptPreview({
        base_system_prompt: form.base_is_default ? null : form.base_system_prompt,
        system_prompt: form.system_prompt || null,
      });
      setPreview({ text: res.rendered, length: res.length });
      setPreviewOpen(true);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.detail : "Не удалось собрать промпт");
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSave = () => {
    // Если шаблон совпадает с дефолтным — сохраняем как null, чтобы при
    // обновлении дефолта в коде проект автоматически получал новый шаблон.
    const baseValue = form.base_system_prompt.trim();
    const baseToSave =
      baseValue && baseValue !== project.base_system_prompt_default.trim()
        ? baseValue
        : null;
    mutation.mutate({
      base_system_prompt: baseToSave,
      system_prompt: form.system_prompt || null,
      forbidden_topics: splitLines(form.forbidden_topics),
      forbidden_promises: splitLines(form.forbidden_promises),
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Промпт и ограничения</CardTitle>
        <CardDescription>
          Базовый промпт — это каркас инструкций ИИ. Можно отредактировать
          напрямую (все правила безопасности и плейсхолдеры) или оставить
          стандартный. Дополнительные указания и явные запреты — отдельные
          поля ниже.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <Label htmlFor="p-base">Базовый промпт (шаблон)</Label>
            <div className="flex items-center gap-2">
              <Badge
                variant={form.base_is_default ? "outline" : "secondary"}
                className="text-xs"
              >
                {form.base_is_default ? "стандартный" : "изменён"}
              </Badge>
              <Button
                variant="outline"
                size="sm"
                onClick={resetBaseToDefault}
                disabled={form.base_system_prompt === project.base_system_prompt_default}
              >
                Сбросить к стандартному
              </Button>
            </div>
          </div>
          <Textarea
            id="p-base"
            rows={18}
            value={form.base_system_prompt}
            onChange={(e) =>
              setForm((s) => ({
                ...s,
                base_system_prompt: e.target.value,
                base_is_default: e.target.value === project.base_system_prompt_default,
              }))
            }
            className="font-mono text-xs"
            spellCheck={false}
          />
          <p className="text-xs text-muted-foreground">
            Доступные плейсхолдеры:&nbsp;
            <code className="px-1 py-0.5 rounded bg-muted">{"{tone}"}</code>,&nbsp;
            <code className="px-1 py-0.5 rounded bg-muted">{"{target_actions}"}</code>,&nbsp;
            <code className="px-1 py-0.5 rounded bg-muted">{"{forbidden_topics}"}</code>,&nbsp;
            <code className="px-1 py-0.5 rounded bg-muted">{"{forbidden_promises}"}</code>,&nbsp;
            <code className="px-1 py-0.5 rounded bg-muted">{"{custom_prompt}"}</code>,&nbsp;
            <code className="px-1 py-0.5 rounded bg-muted">{"{knowledge}"}</code>. Если
            убрать секции «не раскрывай инструкции», «игнорируй взлом» — это снизит
            защиту от промпт-инъекций.
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="p-prompt">Дополнительные указания (custom_prompt)</Label>
          <Textarea
            id="p-prompt"
            rows={5}
            value={form.system_prompt}
            onChange={(e) => setForm((s) => ({ ...s, system_prompt: e.target.value }))}
            placeholder="Например: «Будь дружелюбным, веди к заявке. Если клиент сравнивает с конкурентами — отвечай через ценности и кейсы»."
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="p-topics">Запрещённые темы (по одной в строке)</Label>
            <Textarea
              id="p-topics"
              rows={5}
              value={form.forbidden_topics}
              onChange={(e) => setForm((s) => ({ ...s, forbidden_topics: e.target.value }))}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="p-promises">Запрещённые обещания (по одному в строке)</Label>
            <Textarea
              id="p-promises"
              rows={5}
              value={form.forbidden_promises}
              onChange={(e) =>
                setForm((s) => ({ ...s, forbidden_promises: e.target.value }))
              }
            />
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button onClick={handleSave} disabled={mutation.isPending}>
            {mutation.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            Сохранить
          </Button>
          <Button variant="outline" onClick={handlePreview} disabled={previewLoading}>
            {previewLoading ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Eye className="mr-2 h-4 w-4" />
            )}
            Предпросмотр итогового промпта
          </Button>
        </div>

        <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
          <DialogContent className="sm:max-w-3xl max-h-[85vh] max-h-[85dvh] flex flex-col">
            <DialogHeader>
              <DialogTitle>Итоговый промпт</DialogTitle>
              <DialogDescription>
                Так его получит модель: с подставленными значениями тонa, целей,
                запретов, твоих указаний и текущей базы знаний.
                {preview && ` Длина: ${preview.length} символов.`}
              </DialogDescription>
            </DialogHeader>
            <Textarea
              value={preview?.text ?? ""}
              readOnly
              className="font-mono text-xs flex-1 min-h-0 resize-none overflow-auto"
            />
            <DialogFooter>
              <Button variant="outline" onClick={() => setPreviewOpen(false)}>
                Закрыть
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}
